import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { delay: 0.3 + i * 0.13, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const MaskedLine = ({ children, index }) => (
  <span className="mask-line">
    <motion.span custom={index} variants={lineVariants} initial="hidden" animate="visible">
      {children}
    </motion.span>
  </span>
);

const FloatingPack = ({ src, alt, className, delay, testId }) => (
  <motion.img
    src={src}
    alt={alt}
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
    className={`absolute rounded-[28px] bg-white shadow-[0_24px_48px_-16px_rgba(43,11,60,0.28)] object-contain ${className}`}
    data-testid={testId}
  />
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const packY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  return (
        <section ref={ref} id="top" className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden" data-testid="hero-section">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-12 w-full grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-center">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="inline-block bg-white border border-[#2B0B3C]/15 rounded-full px-5 py-2 text-xs font-bold tracking-[0.15em] uppercase text-[#2B0B3C]/70 mb-8"
            data-testid="hero-overline"
          >
            Proppafoods.com — Snacks, Done Proppa
          </motion.p>
          <h1 className="font-display font-bold uppercase tracking-tight leading-[0.95] text-[#2B0B3C] text-5xl sm:text-7xl lg:text-[6.5rem]" data-testid="hero-heading">
            <MaskedLine index={0}>Crack a</MaskedLine>
            <MaskedLine index={1}>Proppadom.</MaskedLine>
            <MaskedLine index={2}>
              Dip it <span className="text-[#4A148C]">proppa.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-8 max-w-md text-base lg:text-lg font-medium leading-relaxed text-[#2B0B3C]/70"
            data-testid="hero-subtext"
          >
            Hand-finished Proppadoms, proper mango chutney and crunchy Bombay
            mix — delivered to your door, or your pub's back bar.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <a
              href="#range"
              data-testid="hero-shop-cta"
              className="group inline-flex items-center gap-3 bg-[#2B0B3C] text-white rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wide shadow-[0_16px_32px_-12px_rgba(43,11,60,0.45)] hover:bg-[#FF6B00] hover:-translate-y-0.5 transition-[transform,background-color] duration-200"
            >
              Shop the range
              <ArrowUpRight size={18} strokeWidth={3} className="transition-transform duration-200 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <a
              href="#wholesale"
              data-testid="hero-wholesale-cta"
              className="text-sm font-bold uppercase tracking-wide text-[#2B0B3C] underline decoration-[#FF007A] decoration-4 underline-offset-8 hover:text-[#FF007A] transition-colors duration-200"
            >
              Pubs &amp; trade
            </a>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-3"
          >
            {["Free delivery over £30", "Free tee over £50", "Dip pots from £1"].map((chip) => (
              <span key={chip} className="bg-white rounded-full px-4 py-1.5 text-xs font-bold text-[#2B0B3C] shadow-sm">
                {chip}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative h-[420px] sm:h-[480px] lg:h-[72vh]" style={{ perspective: "800px" }}>
          <motion.div style={{ y: packY }} className="absolute inset-0" data-testid="hero-image-frame">
            <FloatingPack
              src="/products/original-studio.jpg"
              alt="Proppa Foods Original Proppadoms packet with the brand mascot"
              className="right-0 top-14 w-[62%] z-20 lg:top-16 lg:w-[74%]"
              delay={0.5}
              rotate={3}
              testId="hero-image"
            />
            <FloatingPack
              src="/products/tamarind-studio.jpg"
              alt="Tamarind Chutney Proppadoms packet"
              className="left-0 bottom-0 w-[36%] z-30 lg:w-[42%]"
              delay={0.8}
              rotate={-8}
              testId="hero-pack-tamarind"
            />
            <FloatingPack
              src="/products/butter-chicken-studio.jpg"
              alt="Butter Chicken Proppadoms packet"
              className="left-1 top-0 w-[30%] z-10 lg:left-2 lg:w-[38%]"
              delay={1.05}
              rotate={-5}
              testId="hero-pack-butter"
            />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#2B0B3C]/50 hidden lg:block"
      >
        <ArrowDown size={20} className="animate-bounce" />
      </motion.div>
    </section>
  );
};
